import React from 'react';

/**
 * Segmented control for switching the *grain* of a page — the same stream of
 * data viewed at a different zoom level (events vs sessions, runs vs spans).
 *
 * This is the shared version of the pill group that EvalSetupPage hand-rolled
 * twice (KindToggle / TargetToggle). New grain switchers should use this so the
 * control looks identical everywhere instead of drifting per feature.
 *
 * Options: `{ value, label, icon?, hint? }` where `icon` is a render function
 * taking `{ size }` — that keeps this file free of any icon library import, so
 * callers can pass a lucide component or the app's <Icon />.
 */
const SIZES = {
  // Standalone, in the body of a page — matches the evals setup toggles.
  md: {
    pill: 'h-7 px-3 text-[12px]',
    wrap: 'border border-[color:var(--border-default)]',
    iconPx: 13,
  },
  // Compact, for the GlobalTopBar's actions slot. That bar is h-8 with pt-1,
  // so only 28px of content box: an h-6 pill plus p-0.5 lands exactly on 28,
  // and a 1px border would push it back out. The sunken fill alone reads as a
  // group here, so the border is dropped rather than shrinking the pill below
  // the h-6 the bar's other action buttons use.
  sm: { pill: 'h-6 px-2.5 text-[12px]', wrap: '', iconPx: 12 },
};

const SegmentedTabs = ({
  options = [],
  value,
  onChange,
  ariaLabel = 'View',
  className = '',
  size = 'md',
}) => {
  if (!options.length) return null;
  const { pill, wrap, iconPx } = SIZES[size] || SIZES.md;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex rounded-md bg-[color:var(--surface-sunken)] p-0.5 ${wrap} ${className}`}
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            title={o.hint || undefined}
            onClick={() => {
              // Re-clicking the active tab is a no-op rather than a re-navigate,
              // so a stray click can't blow away the view's in-flight fetches.
              if (!on) onChange(o.value);
            }}
            className={`inline-flex items-center gap-1.5 rounded font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] ${pill} ${
              on
                ? 'bg-[color:var(--surface-base)] text-[color:var(--text-primary)] shadow-token-sm'
                : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
            }`}
          >
            {o.icon ? o.icon({ size: iconPx }) : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedTabs;
